import type { StaticImageData } from "next/image";
import { useState } from "react";
import { ArrowUpRight } from "lucide-react";

import step1Desktop from "@assets/images/hsa-fsa/step1-desktop.webp";
import step1Mobile from "@assets/images/hsa-fsa/step1-mobile.webp";
import step1Sd from "@assets/images/hsa-fsa/step1-sd.webp";
import step1Tablet from "@assets/images/hsa-fsa/step1-tablet.webp";
import step2Desktop from "@assets/images/hsa-fsa/step2-desktop.webp";
import step2Mobile from "@assets/images/hsa-fsa/step2-mobile.webp";
import step2Sd from "@assets/images/hsa-fsa/step2-sd.webp";
import step2Tablet from "@assets/images/hsa-fsa/step2-tablet.webp";
import step3Desktop from "@assets/images/hsa-fsa/step3-desktop.webp";
import step3Mobile from "@assets/images/hsa-fsa/step3-mobile.webp";
import step3Sd from "@assets/images/hsa-fsa/step3-sd.webp";
import step3Tablet from "@assets/images/hsa-fsa/step3-tablet.webp";
import Eyebrow from "@/blueprint/components/Eyebrow/Eyebrow";
import ResponsiveArt, {
  type ArtBreakpoint,
} from "@/blueprint/components/ResponsiveArt/ResponsiveArt";
import ScrollReveal from "@/blueprint/components/ScrollReveal/ScrollReveal";
import Section from "@/blueprint/components/Section/Section";
import Text from "@/blueprint/components/Text/Text";
import HsaFsaExplainerModal from "@/views/hsa-fsa/sections/HsaFsaExplainerModal";
import { useTrack } from "@/analytics/events";
import { HSA_FSA_STEPS } from "@/views/hsa-fsa/hsaFsaContent";
import { cn } from "@/lib/utils";

type StepId = (typeof HSA_FSA_STEPS.steps)[number]["id"];

const MEDIA: Record<StepId, Record<ArtBreakpoint, StaticImageData>> = {
  select: {
    mobile: step1Mobile,
    tablet: step1Tablet,
    sd: step1Sd,
    desktop: step1Desktop,
  },
  eligibility: {
    mobile: step2Mobile,
    tablet: step2Tablet,
    sd: step2Sd,
    desktop: step2Desktop,
  },
  save: {
    mobile: step3Mobile,
    tablet: step3Tablet,
    sd: step3Sd,
    desktop: step3Desktop,
  },
};

const MEDIA_BOX =
  "w-full shrink-0 rounded-l [aspect-ratio:366/321] tab:h-[321px] tab:w-[385px] sd:h-[500px] sd:w-[600px] d:h-[582px] d:w-[700px]";

const MEDIA_BOX_LAST = "[aspect-ratio:366/305] d:w-[900px]";

type Step = (typeof HSA_FSA_STEPS.steps)[number];

const StepMedia = ({ step, isLast }: { step: Step; isLast: boolean }) => (
  <div
    className={cn(
      "relative overflow-hidden",
      MEDIA_BOX,
      isLast && MEDIA_BOX_LAST,
    )}
  >
    {/* TODO(agency): placeholder art until the step artwork is delivered. */}
    <ResponsiveArt
      sources={MEDIA[step.id]}
      alt={step.mediaAlt}
      sizes="(min-width: 1280px) 50vw, 100vw"
      className="absolute inset-0 h-full w-full object-cover"
    />
  </div>
);

const HsaFsaSteps = ({ position }: { position: number }) => {
  const [explainerOpen, setExplainerOpen] = useState(false);
  const track = useTrack();

  return (
    <Section
      id="hsa-fsa-steps"
      position={position}
      ariaLabelledBy="hsa-fsa-steps-heading"
      className="flex flex-col bg-brand-grey"
    >
      <div className="flex flex-col items-center gap-[8px] pt-[72px] tab:gap-[12px] tab:pt-[96px] sd:gap-[16px] sd:pt-[128px] d:gap-[20px]">
        <Text variant="h3" as="h2" className="text-center">
          <span id="hsa-fsa-steps-heading">{HSA_FSA_STEPS.headline}</span>
        </Text>
      </div>

      {HSA_FSA_STEPS.steps.map((step, index) => {
        const isLast = index === HSA_FSA_STEPS.steps.length - 1;
        return (
          <div
            key={step.id}
            className={cn(
              "flex flex-col items-center gap-[20px] px-grid-margin py-[40px] tab:flex-row tab:gap-[24px] tab:py-[56px] sd:gap-[32px] sd:py-[64px] d:gap-[80px] d:py-[80px]",
              isLast && "pb-[72px] tab:pb-[96px] sd:pb-[128px] d:pb-[128px]",
              !step.mediaFirst && "tab:flex-row-reverse",
            )}
          >
            <ScrollReveal className={cn(MEDIA_BOX, isLast && MEDIA_BOX_LAST)}>
              <StepMedia step={step} isLast={isLast} />
            </ScrollReveal>

            <ScrollReveal
              className="flex w-full min-w-0 flex-1 flex-col items-center gap-[40px] text-center tab:items-start tab:gap-[56px] tab:text-left"
              delayMs={80}
            >
              <div className="flex flex-col gap-[12px] tab:gap-[16px] sd:gap-[20px] d:gap-[24px]">
                <div className="flex flex-col gap-[16px]">
                  <Eyebrow
                    variant="eyebrow-lg"
                    tone="inherit"
                    as="p"
                    className="text-brand-blue"
                  >
                    {step.label}
                  </Eyebrow>
                  <Text variant="h4" as="h3">
                    {step.title}
                  </Text>
                </div>
                <Text
                  variant="paragraph-lg"
                  tone="inherit"
                  className="whitespace-pre-line text-tint-black-90"
                >
                  {step.body}
                </Text>
              </div>

              {"ctaLabel" in step ? (
                /* Not Button: Figma draws a label with a trailing arrow chip, not a pill. */
                <button
                  type="button"
                  onClick={() => {
                    setExplainerOpen(true);
                    track("hsa_fsa_explainer_opened", { step_id: step.id });
                  }}
                  className="tap-target flex w-fit items-center gap-[12px] font-ppMori text-paragraph-lg text-content-primary"
                >
                  {step.ctaLabel}
                  <span
                    aria-hidden
                    className="flex size-[24px] shrink-0 items-center justify-center rounded-full bg-content-primary text-content-inverse"
                  >
                    <ArrowUpRight className="size-[11px]" />
                  </span>
                </button>
              ) : null}
            </ScrollReveal>
          </div>
        );
      })}

      <HsaFsaExplainerModal
        open={explainerOpen}
        onClose={() => setExplainerOpen(false)}
      />
    </Section>
  );
};

export default HsaFsaSteps;
